import services from './services';

import { router } from '../../../main';
import checkAppReady from '../../checkAppReady';
import store from '../../store';
import i18n, { routeLocale } from '@/i18n';
import { isFeatureDisabled } from '@/plugins/country/module';

async function setPrerenderedProposition(context, prerenderedState) {
  context.commit('setCurrentProposition', prerenderedState);
  context.commit('noNeedForExtrasLoading');
}

async function setCurrentProposition(
  context,
  { propositionId, residenceIndex = '1' }
) {
  context.commit('setIsLoading');
  context.dispatch('clearCurrentProposition');
  const isMobile = window.innerWidth < 768;

  let actualResidenceIndex = residenceIndex === '1' ? 0 : 1;

  try {
    const fetchedProposition = await services.fetchProposition({
      propositionId,
      isMobile,
      isAuthenticated: context.rootState.app.isAuthenticated
    });

    // Hide inactive propositions that aren't the current users. The modal
    // renders off store state, not the route, so it must be closed too.
    if (!fetchedProposition.active && !fetchedProposition.isOwner) {
      router.replace(i18n.t('path_not_found', routeLocale));
      context.dispatch('ui/closePropositionModal', null, { root: true });
      return false;
    } else if (!fetchedProposition.active && fetchedProposition.isOwner) {
      fetchedProposition.showActivateUsersPropositionBanner = true;
    }

    if (actualResidenceIndex === 1 && !fetchedProposition.residences[1]) {
      actualResidenceIndex = 0;
    }

    context.dispatch('fetchPropositionUser', {
      userId: fetchedProposition.userId
    });

    context.commit('setCurrentProposition', {
      proposition: fetchedProposition,
      residenceIndex: actualResidenceIndex
    });

    await checkAppReady();

    const isAuthenticated = store.getters['app/isAuthenticated'];

    // Both callees swallow their own errors; neither is awaited.
    if (isAuthenticated) {
      context.dispatch('fetchInterestData', { propositionId });
      context.dispatch('fetchFavouriteData', { propositionId });
    } else {
      context.commit('stopInterestLoading');
    }

    return true;
  } catch (e) {
    // Only a 404 means the proposition is actually gone; other failures get
    // a retryable error state. Resolve false so callers stop (KL-3).
    if (e.response?.status === 404) {
      router.replace(i18n.t('path_proposition_not_found', routeLocale));
      context.dispatch('ui/closePropositionModal', null, { root: true });
    } else {
      window.Logger.error(e);
      context.commit('setLoadingFailed');
    }
    return false;
  }
}

async function fetchInterestData(context, { propositionId }) {
  try {
    const userPropositionId =
      context.rootState.myPropositions.primaryProposition.propositionId;
    const { interest = null, receivedSuperInterest = false } =
      (await services.fetchInterest(userPropositionId, propositionId)) || {};
    // A super like seeds the in-memory set the ad page's star reads —
    // Superintresse is the consumables variant's, so control gets none.
    if (
      interest === 2 &&
      context.rootGetters['app/isTestEnabled']('consumables')
    ) {
      context.commit('interests/addSuperLiked', propositionId, { root: true });
    }

    // The same lookup says whether the other party super-liked this swap;
    // the ad page's banner reads it off the proposition.
    context.commit('mapInterestDataToProposition', {
      interestData: {
        interested: interest === null ? null : interest >= 1,
        receivedSuperInterest: !!receivedSuperInterest
      },
      propositionId
    });
  } catch (e) {
    context.commit('stopInterestLoading');
  }
}

async function fetchFavouriteData(context, { propositionId }) {
  try {
    const favouriteData = await services.fetchFavourite(propositionId);
    context.commit('mapFavouriteDataToProposition', {
      favouriteData,
      propositionId
    });
  } catch (e) {
    console.error(e);
  }
}

function setCurrentResidence(context, { index }) {
  context.commit('setCurrentResidence', {
    index
  });
}

// Dispatched fire-and-forget: a rejection here has no catcher and would
// surface as an unhandled 404 rejection (KL-3).
async function fetchPropositionUser(context, { userId }) {
  try {
    const user = await services.fetchUser(userId);
    context.commit('setPropositionUser', {
      user
    });
  } catch (e) {
    window.Logger.error(e);
  }
}

async function getSponsoredPropositions(context, propositionId) {
  if (isFeatureDisabled('EXTRA_VISIBILITY')) return;

  await checkAppReady();
  const userPropositionId =
    context.rootState.myPropositions.primaryProposition.propositionId;

  const pid = propositionId || userPropositionId;
  if (!pid) {
    return;
  }

  // Called from a detached timer, so nothing catches a rejection (KL-3).
  try {
    const sponsored = await services.fetchSponsoredPropositions(pid);
    context.commit('setSponsoredPropositions', { sponsored });
  } catch (e) {
    window.Logger.error(e);
  }
}

async function getPropositionsSiteCount(context) {
  const propositionsSiteCount = await services.fetchPropositionsSiteCount();

  context.commit(
    'setPropositionsSiteCount',
    propositionsSiteCount.Object.NumAds
  );
}

// *** Interest *** //

function setSwapInterest(context, { targetPropositionId, interested }) {
  context.commit('setSwapInterest', {
    targetPropositionId,
    interested
  });
}

function clearCurrentProposition(context) {
  context.commit('clearCurrentProposition');
}

function favouriteSocketUpdate(context, { targetPropositionId, favourite }) {
  const currentPropositionId = context.state.currentProposition.propositionId;

  if (targetPropositionId === currentPropositionId) {
    window.Logger.log('Updating current proposition favourite');
    context.commit('setCurrentFavourite', {
      favourite
    });
  }
}

async function sendTrustpilotReview() {
  await services.sendTrustpilotReview();
}

export default {
  setPrerenderedProposition,
  setCurrentProposition,
  getSponsoredPropositions,
  getPropositionsSiteCount,
  setCurrentResidence,
  setSwapInterest,
  clearCurrentProposition,
  favouriteSocketUpdate,
  fetchInterestData,
  fetchFavouriteData,
  fetchPropositionUser,
  sendTrustpilotReview
};
